var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from 'react';
import { Box, CircularProgress, Stack, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { baseColors } from '../../Themes/colors';
import { openEmlAttachment } from '../../utils/emlAttachmentViewer';
var DEFAULT_ZOOM_PERCENTAGE = 75;
var MIN_ZOOM_PERCENTAGE = 50;
var MAX_ZOOM_PERCENTAGE = 200;
var ZOOM_STEP = 25;
var toolbarIconButtonSx = {
    padding: '4px 6px',
    border: "1px solid ".concat(baseColors.greyPreset[40]),
    borderRadius: '4px',
    color: baseColors.darkPreset[10],
    backgroundColor: baseColors.fullWhite,
    '&:hover:not(:disabled)': {
        color: baseColors.successPreset[30],
        borderColor: baseColors.successPreset[30],
        backgroundColor: baseColors.greyPreset[55],
    },
    '&:disabled': {
        color: baseColors.greyPreset[30],
        borderColor: baseColors.greyPreset[30],
        cursor: 'not-allowed',
    },
};
var DocumentPreviewPanel = function (_a) {
    var title = _a.title, _b = _a.loading, loading = _b === void 0 ? false : _b, previewType = _a.previewType, fileUrl = _a.fileUrl, previewHtml = _a.previewHtml, binaryData = _a.binaryData, jsonData = _a.jsonData, attachments = _a.attachments, onClose = _a.onClose;
    var docxContainerRef = React.useRef(null);
    var _c = React.useState(false), docRenderError = _c[0], setDocRenderError = _c[1];
    var _d = React.useState(DEFAULT_ZOOM_PERCENTAGE), zoomPercentage = _d[0], setZoomPercentage = _d[1];
    var zoomScale = zoomPercentage / 100;
    var zoomedMinWidth = zoomPercentage > 100 ? "".concat(zoomPercentage, "%") : '100%';
    var canZoom = Boolean(!loading && (previewType || fileUrl || previewHtml || binaryData || jsonData));
    var getZoomStyles = React.useCallback(function (origin) {
        if (origin === void 0) { origin = 'top left'; }
        return {
            transform: "scale(".concat(zoomScale, ")"),
            transformOrigin: origin,
            display: 'inline-block',
            willChange: 'transform',
        };
    }, [zoomScale]);
    var setClampedZoom = React.useCallback(function (nextZoom) {
        setZoomPercentage(Math.min(MAX_ZOOM_PERCENTAGE, Math.max(MIN_ZOOM_PERCENTAGE, nextZoom)));
    }, []);
    var handleZoomOut = React.useCallback(function () {
        setClampedZoom(zoomPercentage - ZOOM_STEP);
    }, [setClampedZoom, zoomPercentage]);
    var handleZoomIn = React.useCallback(function () {
        setClampedZoom(zoomPercentage + ZOOM_STEP);
    }, [setClampedZoom, zoomPercentage]);
    React.useEffect(function () {
        setZoomPercentage(DEFAULT_ZOOM_PERCENTAGE);
    }, [title, previewType, fileUrl, previewHtml]);
    var handleDownloadPdf = React.useCallback(function () {
        if (!fileUrl)
            return;
        var link = document.createElement('a');
        link.href = fileUrl;
        link.download = title || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
    }, [fileUrl, title]);
    var handlePrintPdf = React.useCallback(function () {
        if (!fileUrl)
            return;
        var printFrame = document.createElement('iframe');
        printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        printFrame.src = fileUrl;
        var cleanup = function () { return printFrame.remove(); };
        printFrame.onload = function () {
            var _a, _b;
            try {
                (_a = printFrame.contentWindow) === null || _a === void 0 ? void 0 : _a.focus();
                (_b = printFrame.contentWindow) === null || _b === void 0 ? void 0 : _b.print();
            }
            catch (_c) {
                cleanup();
                return;
            }
            if (printFrame.contentWindow) {
                printFrame.contentWindow.onafterprint = cleanup;
            }
            setTimeout(cleanup, 60000);
        };
        document.body.appendChild(printFrame);
    }, [fileUrl]);
    React.useEffect(function () {
        var isCancelled = false;
        var renderWordDocument = function () { return __awaiter(void 0, void 0, void 0, function () {
            var docxPreview, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (previewType !== 'docx' || !binaryData || !docxContainerRef.current) {
                            return [2];
                        }
                        setDocRenderError(false);
                        docxContainerRef.current.innerHTML = '';
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 4, , 5]);
                        return [4, import('docx-preview')];
                    case 2:
                        docxPreview = _a.sent();
                        if (isCancelled || !docxContainerRef.current)
                            return [2];
                        return [4, docxPreview.renderAsync(binaryData, docxContainerRef.current, undefined, {
                                className: 'docx-preview-content',
                                inWrapper: true,
                                ignoreWidth: false,
                                breakPages: true,
                                ignoreFonts: false,
                                useBase64URL: true,
                                trimXmlDeclaration: true,
                            })];
                    case 3:
                        _a.sent();
                        return [3, 5];
                    case 4:
                        error_1 = _a.sent();
                        console.error('Unable to render Word document preview', error_1);
                        if (!isCancelled) {
                            setDocRenderError(true);
                        }
                        return [3, 5];
                    case 5: return [2];
                }
            });
        }); };
        renderWordDocument();
        return function () {
            isCancelled = true;
        };
    }, [binaryData, previewType]);
    var renderPreviewBody = function () {
        if (loading) {
            return (React.createElement(Stack, { alignItems: "center", justifyContent: "center", sx: { flex: 1, gap: 1.5 } },
                React.createElement(CircularProgress, { size: 28, sx: { color: baseColors.successPreset[30] } }),
                React.createElement(Typography, { variant: "body2", color: baseColors.greyPreset[20] }, "Opening document...")));
        }
        if (previewType === 'docx') {
            if (docRenderError) {
                return (React.createElement(Box, { sx: {
                        flex: 1,
                        overflow: 'auto',
                        overflowX: 'auto',
                        p: 2,
                        backgroundColor: '#F8FAFC',
                        '& pre': {
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'Menlo, Monaco, Consolas, monospace',
                            fontSize: 13,
                            lineHeight: 1.55,
                            color: '#1F2937',
                        },
                    } },
                    React.createElement(Box, { sx: __assign(__assign({}, getZoomStyles('top center')), { minWidth: zoomedMinWidth }), dangerouslySetInnerHTML: { __html: previewHtml || '<pre>Unable to render this Word document.</pre>' } })));
            }
            return (React.createElement(Box, { sx: {
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'auto',
                    px: 2,
                    py: 2,
                    backgroundColor: baseColors.greyPreset[55],
                    '& .docx-wrapper': {
                        backgroundColor: 'transparent !important',
                        padding: '0 !important',
                        display: 'flex',
                        justifyContent: 'center',
                    },
                    '& .docx-preview-content': {
                        maxWidth: '100%',
                        width: '100%',
                    },
                    '& .docx': {
                        maxWidth: '100%',
                        margin: '0 auto !important',
                        boxShadow: '0 2px 12px rgba(15, 23, 42, 0.12) !important',
                        borderRadius: '4px',
                        backgroundColor: '#fff',
                    },
                } },
                React.createElement(Box, { ref: docxContainerRef, sx: __assign(__assign({}, getZoomStyles('top center')), { minWidth: zoomedMinWidth }) })));
        }
        if (previewType === 'xlsx') {
            return (React.createElement(Box, { sx: {
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'auto',
                    px: 2,
                    py: 2,
                    backgroundColor: '#F8FAFC',
                    '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: '#fff',
                    },
                    '& th, & td': {
                        border: "1px solid ".concat(baseColors.greyPreset[40]),
                        padding: '8px 10px',
                        textAlign: 'left',
                    },
                    '& img': {
                        maxWidth: '100%',
                        height: 'auto',
                    },
                } },
                React.createElement(Box, { sx: __assign(__assign({}, getZoomStyles('top left')), { minWidth: zoomedMinWidth }), dangerouslySetInnerHTML: { __html: previewHtml || '<p>No preview available.</p>' } })));
        }
        if (previewType === 'text') {
            return (React.createElement(Stack, { sx: { flex: 1, minHeight: 0 } },
                React.createElement(Box, { sx: {
                        flex: 1,
                        overflow: 'auto',
                        overflowX: 'auto',
                        p: 2,
                        backgroundColor: '#F8FAFC',
                        '& pre': {
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'Menlo, Monaco, Consolas, monospace',
                            fontSize: 12,
                            lineHeight: 1.6,
                            color: '#1F2937',
                        },
                        '& b': {
                            fontWeight: 700,
                        },
                        '& p': {
                            margin: '0 0 8px',
                        },
                        '& table': {
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#fff',
                        },
                        '& th, & td': {
                            border: "1px solid ".concat(baseColors.greyPreset[40]),
                            padding: '8px 10px',
                            textAlign: 'left',
                        },
                    } },
                    React.createElement(Box, { sx: __assign(__assign({}, getZoomStyles('top left')), { minWidth: zoomedMinWidth }), dangerouslySetInnerHTML: { __html: previewHtml || '<pre>No report details available.</pre>' } }))));
        }
        if (previewType === 'html') {
            if (!previewHtml) {
                return (React.createElement(Stack, { alignItems: "center", justifyContent: "center", sx: { flex: 1 } },
                    React.createElement(Typography, { variant: "body2", color: baseColors.greyPreset[20] }, "No report available.")));
            }
            return (React.createElement(Stack, { sx: { flex: 1, minHeight: 0 } },
                React.createElement(Box, { sx: {
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        overflowX: 'auto',
                        backgroundColor: '#fff',
                    } },
                    React.createElement(Box, { sx: __assign(__assign({ height: '100%' }, getZoomStyles('top left')), { minWidth: zoomedMinWidth }) },
                        React.createElement(Box, { component: "iframe", srcDoc: previewHtml, title: title, sandbox: "allow-popups", sx: {
                                width: '100%',
                                height: '100%',
                                minHeight: '600px',
                                border: 'none',
                                display: 'block',
                            } }))),
                attachments && attachments.length > 0 && (React.createElement(Stack, { direction: "row", flexWrap: "wrap", gap: 1, sx: {
                        flexShrink: 0,
                        p: 1.5,
                        borderTop: "1px solid ".concat(baseColors.greyPreset[40]),
                        backgroundColor: baseColors.fullWhite,
                    } }, attachments.map(function (attachment, attachmentIndex) { return (React.createElement(Box, { key: "".concat(attachment.filename, "-").concat(attachmentIndex), component: "button", type: "button", onClick: function () { return openEmlAttachment(attachment); }, sx: {
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.75,
                        border: "1px solid ".concat(baseColors.greyPreset[40]),
                        borderRadius: '6px',
                        backgroundColor: baseColors.greyPreset[50],
                        color: baseColors.darkPreset[10],
                        fontSize: 12,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: baseColors.greyPreset[45] },
                    } },
                    "\uD83D\uDCCE ",
                    attachment.filename,
                    React.createElement(Typography, { component: "span", sx: { fontSize: 11, color: baseColors.greyPreset[20] } },
                        "(",
                        attachment.sizeLabel,
                        ")"))); })))));
        }
        if (previewType === 'image' && fileUrl) {
            return (React.createElement(Box, { sx: {
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'auto',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    p: 2,
                    backgroundColor: baseColors.greyPreset[55],
                } },
                React.createElement(Box, { component: "img", src: fileUrl, alt: title, sx: __assign(__assign({}, getZoomStyles('top center')), { minWidth: zoomedMinWidth, width: '100%', maxWidth: 920, height: 'auto', objectFit: 'contain', backgroundColor: '#fff', borderRadius: 1 }) })));
        }
        if (previewType === 'pdf' && fileUrl) {
            var pdfSrc = "".concat(fileUrl.split('#')[0], "#toolbar=0&navpanes=0&zoom=").concat(zoomPercentage);
            return (React.createElement(Box, { sx: { flex: 1, overflow: 'hidden', backgroundColor: baseColors.greyPreset[55] } },
                React.createElement(Box, { component: "iframe", key: "".concat(fileUrl, "-").concat(zoomPercentage), src: pdfSrc, title: title, sx: { width: '100%', height: '100%', border: 'none', display: 'block' } })));
        }
        if (fileUrl) {
            var iframeSrc = fileUrl;
            return (React.createElement(Box, { sx: {
                    flex: 1,
                    overflow: 'auto',
                    overflowX: 'auto',
                    backgroundColor: baseColors.fullWhite,
                } },
                React.createElement(Box, { component: "iframe", key: "".concat(fileUrl, "-").concat(zoomPercentage), src: iframeSrc, title: title, sx: __assign(__assign({}, getZoomStyles('top left')), { width: zoomedMinWidth, height: '100%', minHeight: '100%', border: 'none', backgroundColor: baseColors.fullWhite }) })));
        }
        return (React.createElement(Stack, { alignItems: "center", justifyContent: "center", sx: { flex: 1 } },
            React.createElement(Typography, { variant: "body2", color: baseColors.greyPreset[20] }, "Preview is not available for this document.")));
    };
    return (React.createElement(Stack, { sx: {
            height: '100%',
            minHeight: 0,
            backgroundColor: baseColors.fullWhite,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0px 4px 18px rgba(15, 23, 42, 0.08)',
            transition: 'all 0.3s ease',
        } },
        React.createElement(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", sx: {
                px: 2.5,
                py: 1.75,
                borderBottom: "1px solid ".concat(baseColors.greyPreset[40]),
            } },
            React.createElement(Tooltip, { title: title, arrow: true },
                React.createElement(Typography, { variant: "h6", fontWeight: 700, color: baseColors.darkPreset[10], sx: {
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordWrap: 'break-word',
                        pr: 1,
                    } }, title)),
            React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1, sx: {
                    ml: 'auto',
                    mr: 2,
                } },
                React.createElement(IconButton, { size: "small", onClick: handleZoomOut, disabled: !canZoom || zoomPercentage <= MIN_ZOOM_PERCENTAGE, sx: toolbarIconButtonSx },
                    React.createElement(RemoveIcon, { sx: { fontSize: 16 } })),
                React.createElement(Typography, { sx: {
                        minWidth: '45px',
                        textAlign: 'center',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: baseColors.darkPreset[10],
                    } },
                    zoomPercentage,
                    "%"),
                React.createElement(IconButton, { size: "small", onClick: handleZoomIn, disabled: !canZoom || zoomPercentage >= MAX_ZOOM_PERCENTAGE, sx: toolbarIconButtonSx },
                    React.createElement(AddIcon, { sx: { fontSize: 16 } })),
                previewType === 'pdf' && fileUrl && (React.createElement(React.Fragment, null,
                    React.createElement(Tooltip, { title: "Download", arrow: true },
                        React.createElement(IconButton, { size: "small", onClick: handleDownloadPdf, sx: toolbarIconButtonSx },
                            React.createElement(FileDownloadOutlinedIcon, { sx: { fontSize: 18 } }))),
                    React.createElement(Tooltip, { title: "Print", arrow: true },
                        React.createElement(IconButton, { size: "small", onClick: handlePrintPdf, sx: toolbarIconButtonSx },
                            React.createElement(PrintOutlinedIcon, { sx: { fontSize: 18 } })))))),
            React.createElement(CloseIcon, { onClick: onClose, sx: {
                    cursor: 'pointer',
                    color: baseColors.darkPreset[10],
                    '&:hover': { color: baseColors.successPreset[30] },
                } })),
        renderPreviewBody()));
};
export default DocumentPreviewPanel;
